import type { FC, ReactNode } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import OriginTracing from '@atlassiansox/origin-tracing/origin-tracer';

import type { Appearance as NewButtonAppearance } from '@atlaskit/button/variants/types';
import Button from '@atlaskit/button/default/button';
import AkButton from '@atlaskit/button/custom-theme-button/custom-theme-button';
import type { Appearance } from '@atlaskit/button/old-button/types';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { token } from '@atlaskit/tokens';

import {
	UPSELL_EDITION_PREMIUM_EXPERIENCE,
	UPSELL_EDITION_UPFLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { GradientCTAButton } from '../GradientLooknFeel/GradientCTAButton.tsx';
import { GradientSubtleLinkButton } from '../GradientLooknFeel/GradientSubtleLinkButton.tsx';
import { useUpflowV2ConfluenceStandardFGsExperiment } from '../UpflowV2ConfluenceStandardFGsExperiment/services/useUpflowV2ConfluenceStandardFGsExperiment.ts';
import { UP_FLOW_EP_MESSAGE_ID } from '../constants.ts';
import {
	CommerceDeepLinksTargetEdition,
	useCommerceDeepLinks,
} from '../CommerceUtils/useCommerceDeepLinks.ts';
import { TryStandardUpflowV2ExperimentTreatmentModalLoadable } from '../UpflowV2ConfluenceStandardFGsExperiment/ui/UpflowV2ExperimentTreatmentModalLoadable.tsx';
import type { UpflowV2Touchpoint } from '../UpflowV2ConfluenceStandardFGsExperiment/ui/StandardEditionHighlights.tsx';

import { ConfluenceEdition } from './__types__/waitForEditionChangeQuery.ts';
import { UpFlowCommerceRouterLoadable } from './UpFlowCommerceRouterLoadable.tsx';
import { TimeoutError, waitForEditionChange } from './waitForEditionChange.ts';
import { calculateTestId } from './calculateTestId.ts';
import { useEditionsAnalyticsContext } from './AnalyticsProvider.tsx';
import UpgradeEditionButtonFragment, {
	type UpgradeEditionButtonFragment$data,
} from './__generated__/UpgradeEditionButtonFragment.graphql.ts';
import {
	SUPPORTED_TOUCHPOINTS,
	type SupportedTouchpoint,
} from './StandardUpgradeModal/constants.ts';
import { StandardUpgradeModalLoadable } from './StandardUpgradeModal/StandardUpgradeModalLoadable.tsx';
import type { UpgradeTouchpoint } from './StandardUpgradeModal/StandardEditionHighlights.tsx';
import { upgradeEditionButtonFragmentSpec } from './UpgradeEditionButtonFragment.ts';
import { useUpgradeButtonDetails } from './useUpgradeButtonDetails.ts';

export type UpgradeEditionButtonProps = {
	analyticsProps: {
		actionSubjectId: string;
		attributes: any;
		source: string;
	};
	appearance?: Appearance;
	targetEdition?: ConfluenceEdition;
	children?: ReactNode;
	onUpFlowOpen?: Function;
	onUpgradeButtonClick?: Function;
};

const i18n = defineMessages({
	tryItFree: {
		id: 'change-edition.upgrade.try.free',
		defaultMessage: 'Try it free',
		description: 'The text of the button/link which starts the user onto the path of free trial',
	},
	tryItNow: {
		id: 'change-edition.upgrade.try.now',
		defaultMessage: 'Try it now',
		description: 'The text of the button/link which starts the user onto the path of upgrade',
	},
	tryPremium: {
		id: 'change-edition.upgrade.try.premium',
		defaultMessage: 'Try Premium',
		description:
			'The text of the button/link which starts the user onto the path of upgrade to Premium edition',
	},
});

type UpgradeButtonComponent =
	| typeof GradientCTAButton
	| typeof GradientSubtleLinkButton
	| typeof AkButton;

/**
 * Maps a button appearance to the underlying button component. Lives in this file
 * (rather than the shared module) because the non-gradient fallback uses the
 * grandfathered custom-theme button import already present here; keeping it in this
 * file avoids introducing that deprecated-button usage in a separate file (ratcheting rule).
 */
export const determineButtonComponent = (appearance: Appearance): UpgradeButtonComponent => {
	if (appearance === 'primary') {
		return GradientCTAButton;
	}

	if (appearance === 'subtle-link') {
		return GradientSubtleLinkButton;
	}

	return AkButton;
};

const editionMap: Record<ConfluenceEdition, CommerceDeepLinksTargetEdition> = {
	[ConfluenceEdition.FREE]: CommerceDeepLinksTargetEdition.FREE,
	[ConfluenceEdition.STANDARD]: CommerceDeepLinksTargetEdition.STANDARD,
	[ConfluenceEdition.PREMIUM]: CommerceDeepLinksTargetEdition.PREMIUM,
};

const extractUpsellDeepLink = (deepLink: unknown): string | undefined => {
	// If deepLink is not a string, return error type for debugging
	if (!deepLink) {
		return undefined;
	}

	if (typeof deepLink !== 'string') {
		return `ERROR_TYPE:${typeof deepLink}`;
	}

	// Extract the base URL without query parameters
	return deepLink.split('?')[0];
};

/**
 * @deprecated Please reach out to #cc-ai-growth for details before use. Please read https://hello.atlassian.net/wiki/spaces/CE2/pages/2797298237/Contribution+Consumption+Guidelines+For+Common+Feature+Gate+Upsell+Component
 */
const UpgradeEditionButtonLegacy: FC<UpgradeEditionButtonProps> = ({
	analyticsProps: { actionSubjectId, attributes, source },
	appearance = 'primary',
	children,
	onUpFlowOpen,
	targetEdition = ConfluenceEdition.STANDARD,
	onUpgradeButtonClick,
}: UpgradeEditionButtonProps) => {
	const [openUpFlow, setOpenUpFlow] = useState<boolean>(false);

	const [pollingError, setPollingError] = useState<Error | undefined>();
	const { cloudId, edition: currentEdition, userId } = useSessionData();
	const { createAnalyticsEvent } = useEditionsAnalyticsContext();
	const writeFragmentInterop = useWriteFragmentInterop<UpgradeEditionButtonFragment$data>({
		fragmentSpec: upgradeEditionButtonFragmentSpec,
		fragmentArtifact: UpgradeEditionButtonFragment,
	});

	const experienceTracker = useContext(ExperienceTrackerContext) as any;

	// free -> standard: upflow spa;
	// free/standard -> premium: adminhub
	const showUpFlow = targetEdition === ConfluenceEdition.STANDARD;

	const {
		cohort: upflowV2ExperimentCohort,
		isSiteAdmin,
		isBillingAdmin,
		loading: upflowV2EligibilityHookLoading,
	} = useUpflowV2ConfluenceStandardFGsExperiment({
		skip: !showUpFlow || !openUpFlow,
		touchpointId: source as UpflowV2Touchpoint,
		currentEdition,
		targetEdition,
	});

	const {
		commerceBackendName,
		planConsentDeepLink,
		errorMessage: commerceDeepLinksError,
	} = useCommerceDeepLinks({
		targetEdition: editionMap[targetEdition],
		skipDeepLinks: showUpFlow,
	});

	useEffect(
		() => () => {
			// 1. With upflow enabled, when the user clicks on the upgrade CTA, the
			//    upflow dialog opens and this component unmounts; the upsell experience
			//    continues and experience tracking is subsequently handled by the
			//    UpFlow component.
			// or
			// 2. With upflow DISabled, unmounting here simply means user navigated away
			//    from the upsell UI and we DO want to signal an aborted experience.
			if (!showUpFlow) {
				experienceTracker.abort({
					name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
					reason: 'Canceled by user',
				});
			}
		},
		[experienceTracker, showUpFlow],
	);

	useEffect(() => {
		// Using a TRACK event for tacking the traffic to the UpgradeEditionButton.
		// This will help us gauage Commerce API traffic to compute the deep links.
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'upgradeEditionButton',
				actionSubjectId,
				source,
				attributes: {
					...attributes,
					currentEdition,
					targetEdition,
				},
			},
		}).fire();
		// We only need this to fire once, when the component mounts
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireClickAnalytics = () => {
		if (attributes) {
			attributes.currentEdition = currentEdition;
			attributes.targetEdition = targetEdition;
			attributes.commerceBackend = commerceBackendName;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId,
				source,
				attributes,
			},
		}).fire();

		if (commerceDeepLinksError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'errored',
					actionSubject: 'useCommerceDeepLinks',
					actionSubjectId,
					source,
					attributes: {
						...attributes,
						currentEdition,
						targetEdition,
						upsellDeepLink: extractUpsellDeepLink(planConsentDeepLink),
						errorMessage: commerceDeepLinksError,
					},
				},
			}).fire();
		}
	};

	const onTryWithoutUpFlowClick = async () => {
		if (onUpgradeButtonClick) {
			onUpgradeButtonClick();
		}

		// start polling and reload the page when done
		try {
			const updatedEdition = await waitForEditionChange(currentEdition, writeFragmentInterop);
			experienceTracker.succeed({
				name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
				attributes: {
					currentEdition,
					targetEdition: updatedEdition,
				},
			});

			// Default behavior
			window.location.reload();
		} catch (error) {
			if (error === TimeoutError) {
				// We don't expect the user to always proceed to upgrade in Admin Hub after seeing the estimated billing info.
				experienceTracker.abort({
					name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
					reason: 'Possibly canceled by user',
				});
			} else {
				stopExperienceOnError(UPSELL_EDITION_PREMIUM_EXPERIENCE, error);
				setPollingError(error);
			}
		}
	};

	const onUpFlowClose = () => {
		setOpenUpFlow(false);
	};

	const onTryButtonClick = () => {
		// we always want to fire both the exposure event as well as the click event
		fireClickAnalytics();

		// start the experience tracker
		experienceTracker.start({
			name: showUpFlow ? UPSELL_EDITION_UPFLOW_EXPERIENCE : UPSELL_EDITION_PREMIUM_EXPERIENCE,
			attributes: {
				currentEdition,
				linkClicked: actionSubjectId,
				source,
				targetEdition,
			},
		});

		// then handle the click behaviour based on FF
		if (showUpFlow) {
			setOpenUpFlow(true);
		} else {
			void onTryWithoutUpFlowClick();
		}
	};

	const buttonProps = {
		appearance,
		className: 'primaryButton',
		href: showUpFlow ? undefined : planConsentDeepLink,
		onClick: onTryButtonClick,
		target: showUpFlow ? undefined : '_blank',
		iconBefore:
			appearance === 'primary' ? (
				<EditionsIcon label="" color={token('color.icon.inverse')} />
			) : undefined,
		testId: calculateTestId({ source, actionSubjectId }),
	};

	const upflowV2ExperimentButtonProps = {
		appearance: appearance as NewButtonAppearance,
		testId: 'upflowV2ExperimentLoadingButton',
		isDisabled: true,
		isLoading: true,
		iconBefore: appearance === 'primary' ? EditionsIcon : undefined,
	};

	const buttonChildren = children || <FormattedMessage {...i18n.tryItFree} />;

	const ButtonComponent = determineButtonComponent(appearance);

	const renderModalContent = () => {
		if (upflowV2EligibilityHookLoading) return null;

		if (upflowV2ExperimentCohort === 'variation') {
			return (
				<TryStandardUpflowV2ExperimentTreatmentModalLoadable
					isOpen={openUpFlow}
					closeModal={onUpFlowClose}
					touchpointId={source as UpflowV2Touchpoint}
					targetEdition={targetEdition}
					isBillingAdmin={isBillingAdmin}
					isSiteAdmin={isSiteAdmin}
					currentEdition={currentEdition}
				/>
			);
		}

		return (
			<UpFlowCommerceRouterLoadable
				canChangeEdition // this component should only ever be used for site admins
				cloudId={cloudId}
				currentEdition={currentEdition || undefined}
				atlassianAccountId={userId || undefined}
				flow="upgrade"
				product="confluence"
				targetEdition={targetEdition}
				touchpointId={source}
				isExperienceTrackerEnabled
				onClose={
					// Note: onUpFlowOpen function closes the parent component which
					// means we don't need to trigger the onClose.
					onUpFlowOpen ? undefined : onUpFlowClose
				}
				onOpen={onUpFlowOpen}
				epMessageId={UP_FLOW_EP_MESSAGE_ID}
			/>
		);
	};

	return (
		<>
			{pollingError ? <ErrorDisplay error={pollingError} /> : null}
			{upflowV2EligibilityHookLoading ? (
				<Button {...upflowV2ExperimentButtonProps}>{buttonChildren}</Button>
			) : (
				<ButtonComponent {...buttonProps}>{buttonChildren}</ButtonComponent>
			)}
			{showUpFlow && openUpFlow ? renderModalContent() : null}
		</>
	);
};

/**
 * Direct-to-Commerce implementation of the upgrade button. Fetches eligibility
 * and the Commerce experience URL, then either opens the URL directly or shows
 * the Standard upgrade modal for supported touchpoints. The whole component is
 * already lazy-loaded at the entry point (entry-points/UpgradeEditionButton.ts
 * is a LoadableAfterPaint), so its query is off the View Page initial-load path.
 */
export const UpgradeEditionButtonDirectToCommerce: FC<UpgradeEditionButtonProps> = ({
	analyticsProps: { actionSubjectId, attributes, source },
	appearance = 'primary',
	children,
	targetEdition = ConfluenceEdition.STANDARD,
	onUpgradeButtonClick,
}: UpgradeEditionButtonProps) => {
	const { cloudId, edition: currentEdition } = useSessionData();
	const { createAnalyticsEvent } = useEditionsAnalyticsContext();
	const [isModalOpen, setModalOpen] = useState(false);
	const hasTrackedVisibleRenderRef = useRef(false);

	const { isBillingAdmin, isSiteAdmin, isAvailableToUser, experienceUrl, loading, error } =
		useUpgradeButtonDetails({
			cloudId,
			targetEdition,
		});

	const origin = useMemo(() => new OriginTracing({ product: 'confluence' }), []);
	const upgradeUrl = useMemo(
		() => (experienceUrl ? origin.addToUrl(experienceUrl) : null),
		[origin, experienceUrl],
	);
	const originAttributes = useMemo(
		() => origin.toAnalyticsAttributes({ hasGeneratedId: true }),
		[origin],
	);

	const shouldShowUpgradeCTA =
		!loading && (isBillingAdmin || isSiteAdmin) && isAvailableToUser && experienceUrl !== null;

	const userRoles = useMemo(() => {
		const roles: string[] = [];
		if (isSiteAdmin) roles.push('siteAdmin');
		if (isBillingAdmin) roles.push('billingAdmin');
		return roles;
	}, [isSiteAdmin, isBillingAdmin]);

	const fireClickAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId,
				source,
				attributes: {
					...attributes,
					...originAttributes,
					currentEdition,
					targetEdition,
					userRoles,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		actionSubjectId,
		source,
		attributes,
		originAttributes,
		currentEdition,
		targetEdition,
		userRoles,
	]);

	const onDirectToCommerceClick = useCallback(() => {
		onUpgradeButtonClick?.();
		fireClickAnalytics();
	}, [onUpgradeButtonClick, fireClickAnalytics]);

	useEffect(() => {
		if (!shouldShowUpgradeCTA || !upgradeUrl || hasTrackedVisibleRenderRef.current) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'upgradeEditionButton',
				actionSubjectId,
				source,
				attributes: {
					...attributes,
					currentEdition,
					targetEdition,
				},
			},
		}).fire();
		hasTrackedVisibleRenderRef.current = true;
	}, [
		shouldShowUpgradeCTA,
		upgradeUrl,
		createAnalyticsEvent,
		actionSubjectId,
		source,
		attributes,
		currentEdition,
		targetEdition,
	]);

	const onModalRouteClick = useCallback(() => {
		onUpgradeButtonClick?.();
		fireClickAnalytics();
		setModalOpen(true);
	}, [onUpgradeButtonClick, fireClickAnalytics]);

	const closeModal = useCallback(() => setModalOpen(false), []);

	useEffect(() => {
		if (loading) {
			return;
		}

		// Two distinct failure modes are reported separately:
		// 1. The eligibility/experience-URL query itself failed.
		// 2. The query succeeded for an eligible user but returned no experience URL.
		let failure: { actionSubjectId: string; errorMessage?: string } | undefined;
		if (error) {
			failure = { actionSubjectId: 'upgradeDetailsQuery', errorMessage: error.message };
		} else if (isAvailableToUser && !upgradeUrl) {
			failure = { actionSubjectId: 'experienceUrl' };
		}

		if (!failure) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'errored',
				actionSubject: 'upgradeEditionButton',
				actionSubjectId: failure.actionSubjectId,
				source,
				attributes: {
					...attributes,
					currentEdition,
					targetEdition,
					...(failure.errorMessage ? { errorMessage: failure.errorMessage } : {}),
				},
			},
		}).fire();
	}, [
		loading,
		error,
		isAvailableToUser,
		upgradeUrl,
		createAnalyticsEvent,
		source,
		attributes,
		currentEdition,
		targetEdition,
	]);

	// Render nothing while the eligibility/experience-URL query is in flight. The
	// data fetch is new behaviour for this path, so a transient disabled button
	// could appear and then disappear for ineligible users; rendering null avoids
	// that flicker.
	if (loading || !shouldShowUpgradeCTA || !upgradeUrl) {
		return null;
	}

	const buttonChildren = children || <FormattedMessage {...i18n.tryItFree} />;
	const ButtonComponent = determineButtonComponent(appearance);
	const commonCtaProps = {
		appearance,
		iconBefore:
			appearance === 'primary' ? (
				<EditionsIcon label="" color={token('color.icon.inverse')} />
			) : undefined,
		testId: calculateTestId({ source, actionSubjectId }),
	};

	const directToCommerceCta = (
		<ButtonComponent
			{...commonCtaProps}
			onClick={onDirectToCommerceClick}
			href={upgradeUrl}
			target="_blank"
			rel="noopener noreferrer"
		>
			{buttonChildren}
		</ButtonComponent>
	);

	if (
		targetEdition === ConfluenceEdition.STANDARD &&
		SUPPORTED_TOUCHPOINTS.includes(source as SupportedTouchpoint)
	) {
		if (
			currentEdition === ConfluenceEdition.FREE &&
			fg('twcg-2033-remove-modals-confluence-upflow')
		) {
			// TWCG-2033 A/A: fires exposure only (no behaviour change) to verify cohort balance and traffic.
			expVal('twcg-2033-remove-modals-confluence-upflow-aa', 'cohort', 'not-enrolled');

			// TWCG-2033 A/B: the variation cohort skips the Standard modal and goes straight to Commerce.
			if (expValEquals('twcg-2033-remove-modals-confluence-upflow-ab', 'cohort', 'variation')) {
				return directToCommerceCta;
			}
		}

		return (
			<>
				<ButtonComponent {...commonCtaProps} onClick={onModalRouteClick}>
					{buttonChildren}
				</ButtonComponent>
				<StandardUpgradeModalLoadable
					isOpen={isModalOpen}
					closeModal={closeModal}
					touchpointId={source as UpgradeTouchpoint}
					targetEdition={targetEdition}
					isBillingAdmin={isBillingAdmin}
					isSiteAdmin={isSiteAdmin}
					currentEdition={currentEdition}
					experienceUrl={upgradeUrl}
				/>
			</>
		);
	}

	return directToCommerceCta;
};

/**
 * Upgrade button router. Picks one of two implementations based on the
 * `confluence_direct_to_commerce_upgrade` feature gate. The router
 * is the only file in this package that references the gate — both
 * implementations are gate-agnostic.
 */
export const UpgradeEditionButton: FC<UpgradeEditionButtonProps> = (props) => {
	if (fg('confluence_direct_to_commerce_upgrade')) {
		return <UpgradeEditionButtonDirectToCommerce {...props} />;
	}
	return <UpgradeEditionButtonLegacy {...props} />;
};
